import { Banner, Checkbox, Select, TextField } from '@shopify/polaris';
import { useShallow } from 'zustand/react/shallow';
import { useWidgetStore } from '../../../store/widget.store';
import { CustomDatePicker } from '../../date-picker';
import { Queries } from '../../../query/query-client';
import { useQuery } from '@tanstack/react-query';
import { TabSection } from '../../tab-section';
import { RichTextComponent } from '../../rich-text.component';
import { WidgetType } from '../../../widgets/widgets';
import {
    countdownEndText,
    countdownTemplate,
    defaultEndText,
    defaultTemplate,
    freeShippingEndText,
    freeShippingTemplate,
    inventoryEndText,
    inventoryTemplate,
} from '../../../widgets/templates';

type Props = {
    widgetType: WidgetType;
};

function widgetExpiryText(widgetType: WidgetType) {
    switch (widgetType) {
        case WidgetType.DEFAULT:
            return 'when the countdown ends';
        case WidgetType.INVENTORY:
            return 'when the stock runs out';
        case WidgetType.FREE_SHIPPING:
            return 'when the free shipping threshold is met';
        default:
            return 'when the countdown ends';
    }
}

export function ContentTab({ widgetType }: Readonly<Props>) {
    const {
        titleText,
        timerType,
        fixedDate,
        fixedMinutes,
        endText,
        onFinish,
        setTitleText,
        setTimerType,
        setFixedDate,
        setFixedMinutes,
        setEndText,
        setOnFinish,
        resumeSessionTimer,
        setResumeSessionTimer,
        freeShippingThreshold,
        setFreeShippingThreshold,
        inventoryStartThreshold,
        setInventoryStartThreshold,
        backgroundColor,
        scrollingText,
        setScrollingText,
    } = useWidgetStore(
        useShallow(state => ({
            titleText: state.titleText,
            timerType: state.timerType,
            fixedDate: state.fixedDate,
            fixedMinutes: state.fixedMinutes,
            endText: state.endText,
            onFinish: state.onFinish,
            setTitleText: state.setTitleText,
            setTimerType: state.setTimerType,
            setFixedDate: state.setFixedDate,
            setFixedMinutes: state.setFixedMinutes,
            setEndText: state.setEndText,
            setOnFinish: state.setOnFinish,
            resumeSessionTimer: state.resumeSessionTimer,
            setResumeSessionTimer: state.setResumeSessionTimer,
            freeShippingThreshold: state.freeShippingThreshold,
            setFreeShippingThreshold: state.setFreeShippingThreshold,
            inventoryStartThreshold: state.inventoryStartThreshold,
            setInventoryStartThreshold: state.setInventoryStartThreshold,
            backgroundColor: state.backgroundColor,
            scrollingText: state.scrollingText,
            setScrollingText: state.setScrollingText,
        }))
    );

    const { data: shopInfo } = useQuery<{
        embeddingEnabled: boolean;
        myshopifyDomain: string;
        appUUID: string | null;
        currencyCode: string;
        currencyFormats: {
            moneyFormat: string;
        };
    }>({
        queryKey: [Queries.SHOP_GET_INFO],
    });

    const populateContent = (type: string) => {
        switch (type) {
            case 'default':
                setTitleText(defaultTemplate);
                setEndText(defaultEndText);
                break;
            case 'countdown':
                setTitleText(countdownTemplate);
                setEndText(countdownEndText);
                break;
            case 'inventory':
                setTitleText(inventoryTemplate);
                setEndText(inventoryEndText);
                break;
            case 'free_shipping':
                setTitleText(freeShippingTemplate);
                setEndText(freeShippingEndText);
                break;
        }
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <TabSection header="Templates">
                <Select
                    label=""
                    value=""
                    onChange={populateContent}
                    options={[
                        {
                            label: 'Select a template',
                            value: '',
                            disabled: true,
                        },
                        {
                            label: 'Default',
                            value: 'default',
                        },
                        {
                            label: 'Countdown Timer',
                            value: 'countdown',
                        },
                        {
                            label: 'Inventory Alert',
                            value: 'inventory',
                        },
                        {
                            label: 'Free Shipping Alert',
                            value: 'free_shipping',
                        },
                    ]}
                />
            </TabSection>
            <TabSection header="Content">
                <div>
                    <RichTextComponent text={titleText} setText={setTitleText} bgColor={backgroundColor} />
                </div>

                <div style={{ marginTop: '6px' }}>
                    <Banner title="Available Macros">
                        <ul style={{ margin: 0 }}>
                            <li>{'{{countdown}} - countdown timer'}</li>
                            <li>{'{{inventory}} - current inventory level'}</li>
                            <li>{'{{free_shipping}} - amount left for free shipping'}</li>
                        </ul>
                        <i>Only one macro type can be used at a time</i>
                    </Banner>
                </div>

                <Checkbox label="Make scrollable text" checked={scrollingText} onChange={setScrollingText} />
            </TabSection>
            {[WidgetType.COUNTDOWN, WidgetType.INVENTORY, WidgetType.FREE_SHIPPING].includes(widgetType) && (
                <TabSection header="Conditions">
                    {widgetType === WidgetType.COUNTDOWN && (
                        <>
                            <div>
                                <Select
                                    label="Timer type"
                                    value={timerType}
                                    onChange={setTimerType}
                                    options={[
                                        { label: 'Deadline countdown', value: 'fixed_date' },
                                        { label: 'Duration countdown', value: 'fixed_minutes' },
                                    ]}
                                />
                            </div>
                            {timerType === 'fixed_minutes' && (
                                <>
                                    <div style={{ marginTop: '12px' }}>
                                        <TextField
                                            label="Minutes"
                                            value={String(fixedMinutes)}
                                            onChange={v => setFixedMinutes(Number(v) || 0)}
                                            autoComplete="off"
                                        />
                                    </div>
                                    <Checkbox
                                        label="Resume session timer"
                                        checked={resumeSessionTimer}
                                        onChange={setResumeSessionTimer}
                                    />
                                </>
                            )}
                            {timerType === 'fixed_date' && (
                                <div style={{ marginTop: '12px' }}>
                                    <CustomDatePicker
                                        title="End date"
                                        value={fixedDate as string}
                                        onChange={setFixedDate}
                                        withTime={true}
                                    />
                                </div>
                            )}
                        </>
                    )}
                    {widgetType === WidgetType.FREE_SHIPPING &&
                        shopInfo?.currencyCode &&
                        shopInfo?.currencyFormats?.moneyFormat && (
                            <div>
                                <TextField
                                    label="Free Shipping Threshold Amount"
                                    type="number"
                                    min={0}
                                    value={freeShippingThreshold ? String(freeShippingThreshold) : '0'}
                                    onChange={v => {
                                        setFreeShippingThreshold(Number(v) || 0);
                                    }}
                                    prefix={shopInfo.currencyFormats.moneyFormat.replace('{{amount}}', '')}
                                    autoComplete="off"
                                />
                            </div>
                        )}
                    {widgetType === WidgetType.INVENTORY && (
                        <div>
                            <TextField
                                label="Show Countdown When Stock Falls Below"
                                type="number"
                                min={0}
                                value={inventoryStartThreshold ? String(inventoryStartThreshold) : '0'}
                                onChange={v => {
                                    setInventoryStartThreshold(Number(v) || 0);
                                }}
                                autoComplete="off"
                            />
                        </div>
                    )}
                </TabSection>
            )}
            {[WidgetType.COUNTDOWN, WidgetType.INVENTORY, WidgetType.FREE_SHIPPING].includes(widgetType) && (
                <TabSection header="When the countdown ends">
                    <div>
                        <Select
                            label={widgetExpiryText(widgetType)}
                            value={onFinish}
                            onChange={setOnFinish}
                            options={[
                                { label: 'Hide', value: 'hide' },
                                { label: 'Do nothing', value: 'do_nothing' },
                                { label: 'Show message', value: 'show_text' },
                            ]}
                        />
                    </div>
                    {onFinish === 'show_text' && (
                        <div style={{ marginTop: '12px' }}>
                            <RichTextComponent text={endText} setText={setEndText} bgColor={backgroundColor} />
                        </div>
                    )}
                </TabSection>
            )}
        </div>
    );
}
